<template>
    <div class="helpdesk-index ppv2-space-y-6">
        <!-- Top Info Banner -->
        <InfoBanner variant="warning" customClass="ppv2-mb-6 !ppv2-py-2 !ppv2-rounded-[10px]">
            {{ $t('helpdesk.index.info_banner.label') }}
            <span v-if="tickets.length > 0"> {{ $t('helpdesk.index.info_banner.has_tickets') }}</span>
            <span v-else>
                {{ $t('helpdesk.index.info_banner.no_tickets') }}
            </span>
        </InfoBanner>

        <!-- Header Section -->
        <div
            class="ppv2-flex md:ppv2-flex-row ppv2-flex-col ppv2-justify-between ppv2-items-start"
            :style="{ gap: normalizeSize(16, true) }"
        >
            <div>
                <Header
                    :title="$t('helpdesk.index.title')"
                    :customFontSize="24"
                    :subtitle="tickets.length > 0 ? $t('helpdesk.index.subtitle') : ''"
                />
            </div>
            <button
                type="button"
                class="ppv2-min-h-[40px] ppv2-flex ppv2-items-center !ppv2-text-white hover:ppv2-text-white ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px] ppv2-py-2 ppv2-rounded-xl ppv2-bg-[#7D2DBD] ppv2-text-white hover:ppv2-bg-[#6b26a3] ppv2-transition-all ppv2-shadow-lg"
                @click="openCreatePanel"
            >
                <Plus :size="17" />
                <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ $t('helpdesk.index.create_button') }}
                </span>
            </button>
        </div>

        <!-- Content Area -->
        <div class="ppv2-w-full !ppv2-px-0">
            <!-- Loading state -->
            <div v-if="loading" class="ppv2-text-center ppv2-py-12">
                <i class="fa fa-spinner fa-spin ppv2-text-3xl ppv2-text-gray-400"></i>
                <p class="ppv2-mt-4 ppv2-text-gray-500">{{ $t('helpdesk.index.loading') }}</p>
            </div>

            <div class="ppv2-w-full" v-else-if="tickets.length > 0">
                <div
                    v-for="ticket in tickets"
                    :key="ticket.id"
                    class="ppv2-border-t ppv2-border-[#E8EDF5] ppv2-py-1 ppv2-flex ppv2-items-center ppv2-justify-between ppv2-transition-all cursor-pointer"
                    @click="viewTicketDetails(ticket)"
                >
                    <div
                        class="ticket-card ppv2-w-full ppv2-flex ppv2-items-center ppv2-justify-between ppv2-border-[#E8EDF5] ppv2-p-3"
                        :style="{ '--hover-bg-color': getCategoryBg(ticket.category) }"
                    >
                        <div class="ppv2-flex ppv2-items-center ppv2-flex-1 ppv2-min-w-0">
                            <!-- ID and Icon -->
                            <div
                                class="ppv2-flex ppv2-items-center"
                                :style="{ gap: normalizeSize(16, true), marginRight: normalizeSize(24, true) }"
                            >
                                <span class="ppv2-text-gray-400 ppv2-text-xs ppv2-w-12">#{{ ticket.id }}</span>
                                <div
                                    class="ppv2-w-10 ppv2-h-10 ppv2-rounded-lg ppv2-flex ppv2-items-center ppv2-justify-center"
                                    :style="{ backgroundColor: getCategoryBg(ticket.category) }"
                                >
                                    <component
                                        :is="getCategoryIcon(ticket.category)"
                                        :size="20"
                                        :color="getCategoryColor(ticket.category)"
                                    />
                                </div>
                            </div>

                            <!-- Subject and Category -->
                            <div class="ppv2-flex ppv2-flex-col ppv2-min-w-0">
                                <h4 class="ppv2-text-sm poppinsMedium ppv2-text-black ppv2-truncate ppv2-mb-0.5">
                                    {{ ticket.subject }}
                                </h4>
                                <span class="ppv2-text-xs poppinsRegular ppv2-text-gray-400">{{ ticket.category }}</span>
                            </div>
                        </div>

                        <!-- Status and Date -->
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-8">
                            <span class="status-tag" :class="getStatusClass(ticket.status)">
                                {{ getStatusLabel(ticket.status) }}
                            </span>
                            <span class="ppv2-text-sm ppv2-text-gray-400 sm:ppv2-w-32 ppv2-text-right">
                                {{ formatRelativeDate(ticket.created_at) }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Empty state -->
            <div
                v-else
                class="ppv2-w-full ppv2-text-center ppv2-min-h-[50vh] ppv2-flex ppv2-flex-col ppv2-justify-center ppv2-items-center"
            >
                <div class="ppv2-mb-6 ppv2-flex ppv2-justify-center">
                    <img src="/media/svg/noTicket.svg" alt="No Ticket" />
                </div>
                <h3 class="ppv2-text-black ppv2-mb-2" :style="{ fontSize: normalizeFontSize(18) }">
                    {{ $t('helpdesk.index.empty_title') }}
                </h3>
                <p class="ppv2-text-black ppv2-mb-6" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('helpdesk.index.empty_subtitle') }}
                    <a href="#" @click.prevent="openCreatePanel" class="ppv2-text-black ppv2-underline">{{
                        $t('helpdesk.index.create_button')
                    }}</a>
                </p>
                <div class="ppv2-border-gray-100">
                    <p
                        class="ppv2-flex ppv2-justify-center ppv2-items-center ppv2-gap-1 ppv2-text-gray-400 ppv2-max-w-[260px] ppv2-mx-auto"
                        :style="{ fontSize: normalizeFontSize(12) }"
                    >
                        {{ $t('helpdesk.index.empty_note') }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Panel for creating ticket -->
        <helpdesk-form ref="helpdeskForm" :user="user" @closed="onFormClosed"></helpdesk-form>
    </div>
</template>

<script>
import HelpdeskForm from './HelpdeskForm.vue'
import InfoBanner from './Shared/InfoBanner.vue'
import Header from './Shared/Header.vue'
import { Plus, Ticket as TicketIcon, FileText, Bug, CircleHelp, Lightbulb } from 'lucide-vue'
import moment from 'moment'
import normalizeSize from '../utils/normalizeSize'
import normalizeFontSize from '../utils/normalizeFontSize'

export default {
    name: 'HelpdeskIndex',
    components: {
        HelpdeskForm,
        InfoBanner,
        Header,
        Plus,
        TicketIcon,
        FileText,
        Bug,
        CircleHelp,
        Lightbulb,
    },
    props: {
        user: {
            type: Object,
            required: true,
        },
        ticketId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            showForm: false,
            tickets: [],
            loading: false,
            selectedTicket: null,
            viewMode: false,
        }
    },
    mounted() {
        this.fetchTickets()
    },
    watch: {
        tickets: {
            handler() {
                if (this.ticketId && this.tickets.length > 0) {
                    const ticket = this.tickets.find((t) => t.id == this.ticketId)
                    if (ticket) {
                        this.viewTicketDetails(ticket)
                    }
                }
            },
            immediate: true,
        },
    },
    methods: {
        normalizeSize,
        normalizeFontSize,
        async fetchTickets() {
            this.loading = true
            try {
                const url = `/api/v1/helpdesk/tickets?user_id=${this.user.id}`
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })

                if (response.ok) {
                    const data = await response.json()
                    if (data.success) {
                        this.tickets = data.tickets
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
            }
        },

        openCreatePanel() {
            this.showForm = true
            this.$nextTick(() => {
                if (this.$refs.helpdeskForm) {
                    this.$refs.helpdeskForm.openPanel()
                }
            })
        },

        viewTicketDetails(ticket) {
            this.selectedTicket = ticket
            this.viewMode = true
            this.$nextTick(() => {
                if (this.$refs.helpdeskForm) {
                    this.$refs.helpdeskForm.openPanel(ticket)
                }
            })
        },

        onFormClosed() {
            this.showForm = false
            this.viewMode = false
            this.selectedTicket = null
            // Refresh tickets list after creating a new ticket
            this.fetchTickets()
        },

        getCategoryIcon(category) {
            const icons = {
                'Problème technique': 'Bug',
                'Abonnement / Facturation PlayPro': 'FileText',
                "Proposition d'amélioration": 'Lightbulb',
                Onboarding: 'CircleHelp',
            }
            return icons[category] || 'CircleHelp'
        },

        getCategoryBg(category) {
            const colors = {
                'Problème technique': '#f7000017', // Red-100
                'Abonnement / Facturation PlayPro': '#095de617', // Blue-100
                "Proposition d'amélioration": '#2cc76617', // Amber-100
                Onboarding: '#7303dd17', // Purple-100
            }
            return colors[category] || '#F3F4F6'
        },

        getCategoryColor(category) {
            const colors = {
                'Problème technique': '#EF4444', // Red-500
                'Abonnement / Facturation PlayPro': '#3B82F6', // Blue-500
                "Proposition d'amélioration": '#16A34A', // Amber-500
                Onboarding: '#A855F7', // Purple-500
            }
            return colors[category] || '#9CA3AF'
        },

        getStatusLabel(status) {
            const labels = {
                active: this.$t('helpdesk.index.statuses.active'),
                pending: this.$t('helpdesk.index.statuses.pending'),
                closed: this.$t('helpdesk.index.statuses.closed'),
            }
            return labels[status] || this.$t('helpdesk.index.statuses.active')
        },

        getStatusClass(status) {
            const classes = {
                active: 'status-open',
                pending: 'status-pending',
                closed: 'status-resolved',
            }
            return classes[status] || 'status-open'
        },

        formatDate(dateString) {
            const date = new Date(dateString)
            return date.toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            })
        },

        formatRelativeDate(dateString) {
            try {
                return moment(dateString).locale(this.$i18n.locale).fromNow()
            } catch (e) {
                return dateString
            }
        },

        stripHtml(html) {
            const tempDiv = document.createElement('div')
            tempDiv.innerHTML = html
            const text = tempDiv.textContent || tempDiv.innerText || ''
            return text.replace(/\s+/g, ' ').trim()
        },
    },
}
</script>

<style scoped lang="scss">
.ticket-card:hover {
    background-color: var(--hover-bg-color);
    box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.02);
    border-radius: 8px;
}

.status-tag {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: capitalize;
}

.status-open {
    background-color: #1575f114;
    color: #3b82f6;
}

.status-pending {
    background-color: #ffe22c14;
    color: #f59e0b;
}

.status-resolved {
    background-color: #08ff5417;
    color: #22c55e;
}

.poppinsBold {
    font-family: 'Poppins', sans-serif;
    font-weight: 700;
}

.poppinsSemiBold {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
}

.poppinsRegular {
    font-family: 'Poppins', sans-serif;
    font-weight: 400;
}
</style>
